import {useMutation} from '@github-ui/react-query'
import {createSubIssueFromChecklistItemMutation as createSubIssueFromChecklistItemQueryId} from '@github-ui/server-persisted-queries'
import type {CreateSubIssueFromChecklistItemMutationMutation} from '@github-ui/server-persisted-queries/types/createSubIssueFromChecklistItemMutation'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'
import {createOperationDescriptor, getRequest} from 'relay-runtime'

import type {
  ConvertChecklistItemToSubIssueInput,
  createSubIssueFromChecklistItemMutation,
  createSubIssueFromChecklistItemMutation$data,
} from './__generated__/createSubIssueFromChecklistItemMutation.graphql'
import createSubIssueFromChecklistItemMutationRequest from './__generated__/createSubIssueFromChecklistItemMutation.graphql'
import type {ChecklistItemConversionRoute} from './checklist-item-conversion-helpers'
import {
  installIssueBodyInRelay,
  postChecklistItemConversion,
  settleIssueFields,
  throwPayloadErrors,
} from './checklist-item-conversion-helpers'

type MutationProps = {
  environment: Environment
  input: ConvertChecklistItemToSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: createSubIssueFromChecklistItemMutation$data) => void
}
export function commitCreateSubIssueFromChecklistItemMutation({
  environment,
  input: {body, repositoryId, parentIssueId, position},
  onError,
  onCompleted,
}: MutationProps) {
  const inputHash: ConvertChecklistItemToSubIssueInput = {
    body,
    repositoryId,
    parentIssueId,
    position,
  }

  return commitMutation<createSubIssueFromChecklistItemMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation createSubIssueFromChecklistItemMutation($input: ConvertChecklistItemToSubIssueInput!)
      @raw_response_type {
        convertChecklistItemToSubIssue(input: $input) {
          issue {
            id
            parent {
              id
              body
              bodyHTML(unfurlReferences: true)
              repository {
                name
                owner {
                  login
                }
              }
              subIssuesConnection: subIssues {
                totalCount
              }
              subIssues(first: 100) {
                nodes {
                  id
                  closed
                  state
                  stateReason
                  issueDependenciesSummary {
                    blockedBy
                  }
                  assignees(first: 10) {
                    totalCount
                    edges {
                      node {
                        id
                        login
                        avatarUrl
                      }
                    }
                  }
                  url
                  repository {
                    name
                    owner {
                      login
                    }
                  }
                  databaseId
                  number
                  title
                  titleHTML
                  issueType {
                    id
                    name
                    color
                  }
                  subIssuesSummary {
                    total
                    completed
                  }
                  closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
                    totalCount
                  }
                }
              }
              databaseId
              number
              repository {
                nameWithOwner
                visibility
                viewerCanPush
                owner {
                  login
                }
              }
              parent {
                id
              }
            }
            state
            stateReason
            issueDependenciesSummary {
              blockedBy
            }
            assignees(first: 10) {
              totalCount
              edges {
                node {
                  id
                  login
                  avatarUrl
                }
              }
            }
            url
            repository {
              name
              owner {
                login
              }
            }
            databaseId
            number
            title
            titleHTML
            issueType {
              id
              name
              color
            }
            subIssuesSummary {
              total
              completed
            }
            closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
              totalCount
            }
          }
          errors {
            message
          }
        }
      }
    `,
    variables: {
      input: inputHash,
    },
    onError: error => onError && onError(error),
    onCompleted: response => onCompleted && onCompleted(response),
  })
}

export type CreateSubIssueFromChecklistItemVariables = ChecklistItemConversionRoute & {
  body: string
  position: readonly number[]
}

type Issue = NonNullable<
  NonNullable<CreateSubIssueFromChecklistItemMutationMutation['convertChecklistItemToSubIssue']>['issue']
>
type ParentIssue = NonNullable<Issue['parent']>
type SuccessfulIssue = Omit<Issue, 'parent'> & {parent: ParentIssue}
type ConversionResult = {
  data: CreateSubIssueFromChecklistItemMutationMutation
  issue: SuccessfulIssue
}

function subIssueFromResponse(data: CreateSubIssueFromChecklistItemMutationMutation): SuccessfulIssue {
  const payload = data.convertChecklistItemToSubIssue
  if (!payload) throw new Error('Create sub-issue from checklist item returned no payload')
  throwPayloadErrors(payload.errors, 'convertChecklistItemToSubIssue')

  const issue = payload.issue
  if (!issue) throw new Error('Create sub-issue from checklist item returned no issue')
  if (!issue.parent) throw new Error('Create sub-issue from checklist item returned no parent issue')

  return {...issue, parent: issue.parent}
}

export function useCreateSubIssueFromChecklistItemMutation(environment: Environment) {
  return useMutation<ConversionResult, Error, CreateSubIssueFromChecklistItemVariables>({
    mutationFn: async ({owner, repository, issueNumber, body, position}) => {
      const input = {body, position: [...position]}
      const data = await postChecklistItemConversion<CreateSubIssueFromChecklistItemMutationMutation, typeof input>(
        {owner, repository, issueNumber},
        'checklist_item_sub_issues',
        input,
        createSubIssueFromChecklistItemQueryId,
      )

      return {data, issue: subIssueFromResponse(data)}
    },
    onSuccess: async ({data, issue}, {body, position}) => {
      const relayInput: ConvertChecklistItemToSubIssueInput = {
        body,
        parentIssueId: issue.parent.id,
        position: [...position],
        repositoryId: issue.repository.id,
      }
      const operation = createOperationDescriptor(getRequest(createSubIssueFromChecklistItemMutationRequest), {
        input: relayInput,
      })
      environment.commitPayload(operation, data)
      installIssueBodyInRelay(environment, issue.parent)

      const {id: parentId, body: parentBody, bodyHTML, subIssues, subIssuesConnection} = issue.parent
      const {id: issueId, ...issueFields} = issue

      await settleIssueFields({
        type: 'setFields',
        id: parentId,
        fields: {body: parentBody, bodyHTML, subIssues, subIssuesConnection},
      })
      await settleIssueFields({
        type: 'setFields',
        id: issueId,
        fields: issueFields,
      })
    },
  })
}
